import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, LogOut, Globe, ChevronDown, ChevronRight, Building2, Briefcase, BarChart3, Plus, Users, MessageSquare } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { useUnreadMessages } from "../context/UnreadMessagesContext";
import { CandidateAvatar } from "./CandidateAvatar";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { BrandLogo } from "./BrandLogo";

// Shared exact size for BOTH the header logo and the drawer logo — same value
// used in both places guarantees identical visual scale (Round: logo unify).
const BRAND_LOGO_HEIGHT = "clamp(38px, 10vw, 48px)";
const LANGUAGES = [
  { code: "es", label: "Español", flag: "🇪🇸" },
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "pt", label: "Português", flag: "🇧🇷" },
];

// Compact fixed top bar for guests + candidates on mobile — logo, language,
// hamburger only. Secondary/account nav lives entirely in the drawer.
export default function MobileHeader() {
  const { user, logout } = useAuth();
  const { t, language, changeLanguage } = useLanguage();
  const { unreadCount } = useUnreadMessages();
  const unreadBadge = unreadCount > 99 ? "99+" : unreadCount > 0 ? String(unreadCount) : null;
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  const currentLang = LANGUAGES.find((l) => l.code === language) || LANGUAGES[0];

  const handleLogout = () => {
    // Navigate away first and wait a full paint cycle (double rAF) before
    // clearing the user — a bare setTimeout(0) can still fire before React
    // Router actually swaps UserDashboard out, letting its own !user guard
    // effect race us back to /auth instead of home.
    setOpen(false);
    navigate("/", { replace: true });
    requestAnimationFrame(() => requestAnimationFrame(() => logout()));
  };

  // Secondary items only — the 5 primary candidate areas already live in the
  // Bottom Nav (Inicio/Mis empleos/Mensajes/Quién me ve/Perfil), so this drawer
  // is never a second copy of it. Plain "text + chevron" rows, section headers,
  // no per-row icons (icons are reserved for the Bottom Nav only).
  const candidateSections = [
    {
      title: t("mobileHeader.sectionAccount"),
      items: [
        { to: "/dashboard?tab=profile", label: t("mobileHeader.myProfile"), testid: "drawer-link-my-profile" },
        { to: "/dashboard?tab=whoviewedme", label: t("bottomNav.whoViewedMe"), testid: "drawer-link-who-viewed-me" },
        { to: "/company-view", label: t("profile.viewPublic"), testid: "drawer-link-company-view" },
        { to: "/dashboard?tab=settings", label: t("mobileHeader.settings"), testid: "drawer-link-settings" },
      ],
    },
    {
      title: t("mobileHeader.sectionExplore"),
      items: [
        { to: "/companies", label: t("nav.companies"), testid: "drawer-link-companies" },
      ],
    },
    {
      title: t("mobileHeader.sectionHelp"),
      items: [
        { to: "/contact", label: t("mobileHeader.help"), testid: "drawer-link-help" },
      ],
    },
  ];
  // Employer drawer = the same 5 dashboard sections as its own bottom nav
  // (Resumen/Publicar/Anuncios/Candidatos/Perfil) plus Mensajes — never the
  // candidate/guest public-site links (Inicio/Buscar Empleos/Empresas), since
  // an authenticated employer has no use for the public job search landing.
  const employerLinks = [
    { to: "/company/dashboard?tab=overview", label: t("tabs.overview"), icon: BarChart3, testid: "drawer-link-overview" },
    { to: "/company/dashboard?tab=post", label: t("tabs.postJob"), icon: Plus, testid: "drawer-link-post-job" },
    { to: "/company/dashboard?tab=jobs", label: t("tabs.myListings"), icon: Briefcase, testid: "drawer-link-my-listings" },
    { to: "/company/dashboard?tab=applicants", label: t("tabs.applicants"), icon: Users, testid: "drawer-link-applicants" },
    { to: "/company/dashboard?tab=company", label: t("tabs.companyProfile"), icon: Building2, testid: "drawer-link-company-profile" },
    { to: "/messages", label: t("bottomNav.messages"), icon: MessageSquare, testid: "drawer-link-messages" },
  ];
  const guestLinks = [
    { to: "/companies", label: t("nav.companies"), icon: Building2, testid: "drawer-link-companies" },
    { to: "/auth?tab=register", label: t("mobileHeader.hiringCta"), icon: Briefcase, testid: "drawer-link-hiring" },
  ];

  return (
    <>
      <header
        data-testid="mobile-header"
        className="lg:hidden fixed top-0 inset-x-0 z-40 bg-white border-b border-slate-200 flex items-center px-2"
        style={{ height: "calc(4rem + env(safe-area-inset-top))", paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="w-14 flex items-center justify-start flex-shrink-0">
          <button
            data-testid="mobile-header-menu-button"
            aria-label={t("mobileHeader.menu")}
            onClick={() => setOpen(true)}
            className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors flex-shrink-0"
          >
            <Menu className="h-5 w-5 text-slate-700" />
          </button>
        </div>

        <Link to={user?.role === "employer" ? "/company/dashboard" : "/"} data-testid="mobile-header-logo-link" className="flex-1 flex items-center justify-center min-w-0 px-1">
          <BrandLogo height={BRAND_LOGO_HEIGHT} testId="mobile-header-logo" />
        </Link>

        <div className="w-14 flex items-center justify-end relative flex-shrink-0">
          <button
            data-testid="mobile-header-lang-button"
            aria-label={currentLang.label}
            onClick={() => setLangOpen((v) => !v)}
            className="flex items-center gap-0.5 px-1.5 py-1.5 rounded-lg text-[11px] font-bold text-slate-600 hover:bg-slate-100 uppercase tracking-wide"
          >
            <Globe className="h-3.5 w-3.5 text-slate-400" /> {currentLang.code}
          </button>
          {langOpen && (
            <div className="absolute right-0 top-full mt-1 w-36 bg-white border border-slate-200 rounded-xl shadow-xl py-1.5 z-50" data-testid="mobile-header-lang-menu">
              {LANGUAGES.map(({ code, label, flag }) => (
                <button key={code} data-testid={`mobile-lang-${code}`}
                  onClick={() => { changeLanguage(code); setLangOpen(false); }}
                  className={`flex items-center gap-2 w-full px-3 py-2 text-sm ${language === code ? "text-[#028CF8] font-semibold" : "text-slate-700"}`}>
                  <span>{flag}</span> {label}
                </button>
              ))}
            </div>
          )}
        </div>
      </header>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="left"
          data-testid="mobile-drawer"
          className="w-4/5 max-w-xs flex flex-col p-0"
          style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
        >
          <SheetHeader className="p-5 border-b border-slate-100 text-left">
            {user ? (
              <Link
                to={user?.role === "employer" ? "/company/dashboard?tab=company" : "/dashboard?tab=profile"}
                onClick={() => setOpen(false)}
                data-testid="drawer-user-header-link"
                className="flex items-center gap-3"
              >
                <CandidateAvatar name={user?.name} picture={user?.picture} size={44} rounded="xl" />
                <div className="min-w-0">
                  <SheetTitle className="font-poppins text-lg truncate">{user?.name}</SheetTitle>
                  <SheetDescription className="truncate text-[15px]">{user?.email}</SheetDescription>
                </div>
              </Link>
            ) : (
              <div>
                <SheetTitle className="sr-only">InfoCityJobs</SheetTitle>
                <BrandLogo height={BRAND_LOGO_HEIGHT} testId="mobile-drawer-logo" className="mb-2" />
                <SheetDescription className="text-[15px]">{t("mobileHeader.guestSubtitle")}</SheetDescription>
              </div>
            )}
          </SheetHeader>

          {!user && (
            <div className="p-4 flex flex-col gap-2 border-b border-slate-100">
              <Link to="/auth" data-testid="drawer-login-link" onClick={() => setOpen(false)}
                className="h-12 flex items-center justify-center rounded-xl border border-slate-200 text-slate-900 font-semibold text-base font-poppins hover:bg-slate-50 transition-colors">
                {t("nav.signIn")}
              </Link>
              <Link to="/auth?tab=register" data-testid="drawer-register-link" onClick={() => setOpen(false)}
                className="h-12 flex items-center justify-center rounded-xl bg-[#028CF8] hover:bg-[#0270C5] text-white font-semibold text-base font-poppins transition-colors">
                {t("nav.getStarted")}
              </Link>
            </div>
          )}

          <nav className="flex-1 py-2 overflow-y-auto">
            {user?.role !== "employer" && user ? (
              candidateSections.map((section) => (
                <div key={section.title} className="mb-2">
                  <p className="px-5 pt-3 pb-1 text-[11px] font-bold uppercase tracking-wide text-slate-400 font-poppins">{section.title}</p>
                  {section.items.map(({ to, label, testid }) => (
                    <Link key={testid} to={to} data-testid={testid} onClick={() => setOpen(false)}
                      className="flex items-center justify-between gap-3 px-5 py-3 text-base font-medium text-slate-700 hover:bg-slate-50 transition-colors font-poppins border-b border-slate-50 last:border-0">
                      {label}
                      <ChevronRight className="h-4 w-4 text-slate-300 flex-shrink-0" />
                    </Link>
                  ))}
                </div>
              ))
            ) : (
              (user?.role === "employer" ? employerLinks : guestLinks).map(({ to, label, icon: Icon, testid }) => (
                <Link
                  key={testid}
                  to={to}
                  data-testid={testid}
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-3 px-5 py-3.5 text-base font-medium text-slate-700 hover:bg-slate-50 transition-colors font-poppins"
                >
                  <Icon className="h-5 w-5 text-[#028CF8]" />
                  {label}
                  {testid === "drawer-link-messages" && unreadBadge && (
                    <span data-testid="drawer-messages-badge"
                      className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-[#F55FA5] text-white text-[11px] font-bold flex items-center justify-center leading-none">
                      {unreadBadge}
                    </span>
                  )}
                </Link>
              ))
            )}
          </nav>

          {user && (
            <div className="p-3 border-t border-slate-100">
              <button
                data-testid="drawer-logout-button"
                onClick={handleLogout}
                className="flex w-full items-center gap-3 px-2 py-3 text-base font-semibold text-[#F55FA5] hover:bg-[#F55FA5]/10 rounded-lg transition-colors font-poppins"
              >
                <LogOut className="h-5 w-5" /> {t("nav.signOut")}
              </button>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}
